import { commentApi } from "api/comment.api";
import { AppButton } from "components/antd-ui";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { userStore } from "store/userStore";
import { Comment, CommentFile, CommentRefType } from "types/comment";
import { CommentComposer } from "./CommentComposer";
import { CommentItem } from "./CommentItem";
import "./CommentThread.scss";

const PAGE_SIZE = 20;

type CommentThreadProps = {
  refType: CommentRefType;
  refId: number;
  code?: string;
};

const normalize = (comment: Comment): Comment => ({
  ...comment,
  content: comment.content || "",
  files: comment.files || [],
  mentionStaffs: comment.mentionStaffs || [],
  replies: (comment.replies || []).map((reply) => ({
    ...reply,
    content: reply.content || "",
    files: reply.files || [],
    mentionStaffs: reply.mentionStaffs || [],
    replies: [],
  })),
});

export const CommentThread = observer(({ refType, refId, code }: CommentThreadProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [replyId, setReplyId] = useState<number | null>(null);
  const currentStaffId = userStore.info.id;
  const currentName = userStore.info.name || "";
  const currentAvatar = userStore.info.avatar;

  const load = useCallback(async (nextPage: number, append: boolean) => {
    setLoading(true);
    if (!append) setComments([]);
    try {
      const res = await commentApi.findAll({
        page: nextPage,
        limit: PAGE_SIZE,
        refType,
        refId,
      });
      const nextComments: Comment[] = (res.data.comments || []).map(normalize);
      setComments((prev) => (append ? [...prev, ...nextComments] : nextComments));
      setTotal(res.data.total || 0);
      setPage(nextPage);
    } finally {
      setLoading(false);
    }
  }, [refId, refType]);

  useEffect(() => {
    setReplyId(null);
    load(1, false);
  }, [load]);

  const handleCreate = useCallback(async (payload: { content: string; files: CommentFile[]; mentionStaffIds: number[] }) => {
    const res = await commentApi.create({
      comment: {
        content: payload.content,
        files: payload.files,
        refType,
        refId,
      },
      mentionStaffIds: payload.mentionStaffIds,
    });
    setComments((prev) => [normalize(res.data), ...prev]);
    setTotal((prev) => prev + 1);
  }, [refId, refType]);

  const handleReply = useCallback(async (
    parentId: number,
    payload: { content: string; files: CommentFile[]; mentionStaffIds: number[] },
  ) => {
    const res = await commentApi.create({
      comment: {
        content: payload.content,
        files: payload.files,
        refType,
        refId,
      },
      parentId,
      mentionStaffIds: payload.mentionStaffIds,
    });
    const reply = normalize(res.data);
    setComments((prev) => prev.map((comment) => (
      comment.id === parentId
        ? { ...comment, replies: [...comment.replies, reply] }
        : comment
    )));
    setReplyId(null);
  }, [refId, refType]);

  const handleDelete = useCallback(async (commentId: number) => {
    await commentApi.delete(commentId);
    let removedRoot = false;
    setComments((prev) => {
      removedRoot = prev.some((comment) => comment.id === commentId);
      if (removedRoot) return prev.filter((comment) => comment.id !== commentId);
      return prev.map((comment) => ({
        ...comment,
        replies: comment.replies.filter((reply) => reply.id !== commentId),
      }));
    });
    if (removedRoot) setTotal((totalCount) => Math.max(0, totalCount - 1));
    if (replyId === commentId) setReplyId(null);
  }, [replyId]);

  const toggleReply = useCallback((commentId: number) => {
    setReplyId((current) => (current === commentId ? null : commentId));
  }, []);

  return (
    <div className="commentThread">
      <CommentComposer
        avatar={currentAvatar}
        name={currentName}
        placeholder="Bình luận"
        submitLabel="Bình luận"
        onSubmit={handleCreate}
      />
      <div className="commentThread__list">
        {comments.map((comment) => (
          <div key={comment.id}>
            <CommentItem
              comment={comment}
              code={code}
              canDelete={!!currentStaffId && comment.staff?.id === currentStaffId}
              replyOpen={replyId === comment.id}
              replyAvatar={currentAvatar}
              replyName={currentName}
              onToggleReply={() => toggleReply(comment.id)}
              onDelete={handleDelete}
              onReply={(payload) => handleReply(comment.id, payload)}
            />
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                code={code}
                isReply
                canDelete={!!currentStaffId && reply.staff?.id === currentStaffId}
                replyOpen={false}
                replyAvatar={currentAvatar}
                replyName={currentName}
                onToggleReply={() => undefined}
                onDelete={handleDelete}
                onReply={async () => undefined}
              />
            ))}
          </div>
        ))}
        {!loading && !comments.length && (
          <div className="commentThread__empty">Chưa có bình luận</div>
        )}
        {comments.length < total && (
          <AppButton loading={loading} onClick={() => load(page + 1, true)}>
            Xem thêm
          </AppButton>
        )}
      </div>
    </div>
  );
});
